"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Boxes,
  ChevronDown,
  PackageCheck,
  PlusSquare,
  Ticket,
  ClipboardList,
  Building2,
  Clock,
  Truck,
  Eye,
  Percent,
  ToggleRight,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import Image from "next/image";
import ccLogo from "../assets/icons/Cycle-Circle-Logo-Orange-1.png";
import { useEffect, useRef, useState, JSX } from "react";
import gsap from "gsap";

const B2CMobileSidebar = ({
  isSidebarOpen,
  toggleSidebar,
}: {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}) => {
  const pathname = usePathname();
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (navRef.current) {
      gsap.fromTo(
        navRef.current.children,
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.1, ease: "power2.out", duration: 0.6 }
      );
    }
  }, []);

  // Prefetch B2C routes when sidebar opens
  useEffect(() => {
    if (isSidebarOpen) {
      router.prefetch("/select-business");
      router.prefetch("/b2c/dashboard");
      router.prefetch("/b2c/orders");
      router.prefetch("/b2c/pre-orders");
      router.prefetch("/b2c/all-inventory");
      router.prefetch("/b2c/carts");
      router.prefetch("/b2c/brands");
      router.prefetch("/b2c/manage-inventory");
      router.prefetch("/b2c/coupons");
      router.prefetch("/b2c/wallets");
    }
  }, [isSidebarOpen, router]);

  return (
    <div>
      {/* Sidebar */}
      <Card
        className={cn(
          "fixed left-0 top-0 h-screen w-64 bg-gradient-to-b rounded-none from-gray-900 to-gray-800 text-white shadow-xl p-4 z-50 transition-transform duration-300",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <Image
            src={ccLogo}
            priority
            alt="cc-logo"
            width={100}
            height={50}
            className="object-contain"
          />
        </div>

        {/* Navigation Links */}
        <nav ref={navRef} className="space-y-2">
          <div className="pb-2 border-b border-gray-600 mb-5">
            <SidebarLink
              href="/select-business"
              icon={<Building2 size={20} />}
              text="Switch Business"
              pathname={pathname}
              toggleSidebar={toggleSidebar}
            />
          </div>
          <SidebarLink
            href="/b2c/dashboard"
            icon={<LayoutDashboard size={20} />}
            text="Dashboard"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/orders"
            icon={<ClipboardList size={20} />}
            text="Orders"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/pre-orders"
            icon={<Clock size={20} />}
            text="Pre-Orders"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/all-inventory"
            icon={<Boxes size={20} />}
            text="All Inventory"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/carts"
            icon={<ShoppingCart size={20} />}
            text="Carts"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />

          {/* Manage Inventory (Collapsible) */}
          <button
            onClick={() => setIsInventoryOpen(!isInventoryOpen)}
            className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all"
          >
            <div className="flex items-center space-x-3">
              <Boxes size={20} />
              <span className="font-medium">Manage Inventory</span>
            </div>
            <ChevronDown
              size={18}
              className={`transition-transform ${
                isInventoryOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isInventoryOpen && (
            <div className="pl-8 space-y-2">
              <SidebarLink
                href="/b2c/brands"
                icon={<PackageCheck size={16} />}
                text="Configure Brand"
                pathname={pathname}
                toggleSidebar={toggleSidebar}
              />
              <SidebarLink
                href="/b2c/manage-inventory"
                icon={<PlusSquare size={16} />}
                text="Manage Inventory"
                pathname={pathname}
                toggleSidebar={toggleSidebar}
              />
            </div>
          )}

          <SidebarLink
            href="/b2c/collections"
            icon={<Package size={20} />}
            text="Collections"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/coupons"
            icon={<Ticket size={20} />}
            text="Coupons"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/free-shipping"
            icon={<Truck size={20} />}
            text="Free Shipping"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/product-visibility"
            icon={<Eye size={20} />}
            text="Product Visibility"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/sale"
            icon={<Percent size={20} />}
            text="Sale"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2c/switch-to-admin"
            icon={<ToggleRight size={20} />}
            text="Swith to Admin"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
        </nav>
      </Card>
    </div>
  );
};

const SidebarLink = ({
  href,
  icon,
  text,
  pathname,
  toggleSidebar,
}: {
  href: string;
  icon: JSX.Element;
  text: string;
  pathname: string;
  toggleSidebar: () => void;
}) => {
  const isActive = pathname === href;
  const router = useRouter();

  return (
    <button
      onClick={() => {
        toggleSidebar();
        router.push(href);
      }}
      className={cn(
        "w-full cursor-pointer flex items-center space-x-3 px-4 py-3 rounded-lg transition-all group",
        isActive
          ? "bg-white text-black shadow-md"
          : "text-gray-300 hover:bg-gray-700 hover:text-white"
      )}
    >
      <span
        className={cn(
          "transition-all",
          isActive ? "text-black" : "text-gray-400 group-hover:text-white"
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "transition-all font-medium",
          isActive ? "text-black" : "text-gray-300 group-hover:text-white"
        )}
      >
        {text}
      </span>
    </button>
  );
};

export default B2CMobileSidebar;
